import React from 'react';
import clsx from 'clsx';

import {
  AUDIO_CODES_LIST,
  AUDIOCODES_FILETYPE,
  VALIDATIONS,
} from '@shared/constants';
import { writeXLSFile } from '@utils';

import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';

import { Autocomplete, Grid, Dialog, Input } from '@shared/components';
import useStyles from '../style';

const paperElevation = 1;
const DEVICE_NICKNAME_FLAG = 'FXS';
const noop = () => {};
const defaultState = {
  downloadAudioFile: false,
  audioFileName: 'Audio_codes',
  audioFileType: AUDIOCODES_FILETYPE[0],
  audioFileNameError: null,
};
const AudioCodesComponent = ({
  extensionList = [],
  sheetData = [],
  entries = [],
  gatewayModelSelected = '',
  pageSize = 5,
  pageNumber = 1,
  handleExtensionChange = noop,
  handleGatewayChange = noop,
  handlePageSizeChange = noop,
  handlePageNumberChange = noop,
}) => {
  const classes = useStyles();
  const [state, setState] = React.useState(defaultState);

  let options = extensionList
    .filter(
      ext =>
        !entries.some(
          entry => entry.extension.extensionNumber === ext.extensionNumber
        )
    )
    .sort((rowA, rowB) => {
      if (rowA.extensionNumber && rowB.extensionNumber) {
        return rowA.extensionNumber - rowB.extensionNumber;
      }
    });
  options = [
    ...options,
    { name: 'Unassigned Extension', extensionNumber: null },
  ];
  const columns = [
    {
      field: 'portNumber',
      label: 'PORT NUMBER',
    },
    {
      field: 'extension',
      label: 'EXTENSION NUMBER',
      render: (row, index) => {
        return (
          <div className='w-50'>
            <Autocomplete
              fullWidth
              disableClearable
              inputProps={{
                classes: {
                  root:
                    row.extension && row.extension.extensionNumber !== null
                      ? classes.extensionInput
                      : '',
                },
              }}
              getOptionLabel={option => {
                return option.extensionNumber
                  ? `${option.extensionNumber}-${option.name}`
                  : option.name;
              }}
              value={
                row.extension.extensionNumber !== null ? row.extension : null
              }
              options={options}
              placeholder='Unassigned Extension'
              onChange={(event, selectedOption) =>
                handleExtensionChange(selectedOption, row)
              }
            />
          </div>
        );
      },
    },
  ];

  const getPreparedData = () => {
    const data = entries
      .map(item => {
        const row = sheetData.find(
          sipRow => sipRow['Extension Number'] == item.extension.extensionNumber
        );
        return {
          name: row ? row['Device Nick Name'] : '',
          userName: row ? row['Username'] : '',
          password: row ? row['Password'] : '',
          authorizationId: row ? row['Authorization ID'] : '',
          extension: item.extension.extensionNumber,
          hasValidData: !!row,
        };
      })
      .slice(0, gatewayModelSelected?.ports || 0);

    return data;
  };

  const getTrunkGroupData = (data = []) => {
    let trunckData = [];
    if (gatewayModelSelected?.ports > 24) {
      let count = 0;
      let cycle = 0;
      trunckData = data.map((row, index) => {
        count = count >= 72 ? 1 : count + 1;
        cycle = count == 1 ? cycle + 1 : cycle;
        if (!row.hasValidData) return null;
        return {
          'FORMAT TrunkGroup_Index = TrunkGroup_TrunkGroupNum': `TrunkGroup ${index} = 1`,
          TrunkGroup_FirstTrunkId: 255,
          TrunkGroup_FirstBChannel: count,
          TrunkGroup_LastBChannel: count,
          TrunkGroup_FirstPhoneNumber: row.userName,
          TrunkGroup_ProfileName: 'defaultTelProfile',
          TrunkGroup_LastTrunkId: 255,
          'TrunkGroup_Module;': `${cycle};`,
        };
      });
    } else {
      trunckData = data.map((row, index) => {
        if (!row.hasValidData) return null;
        return {
          'FORMAT TrunkGroup_Index = TrunkGroup_TrunkGroupNum': `TrunkGroup ${index} = 1`,
          TrunkGroup_FirstTrunkId: 255,
          TrunkGroup_FirstBChannel: index + 1,
          TrunkGroup_LastBChannel: index + 1,
          TrunkGroup_FirstPhoneNumber: row.userName,
          TrunkGroup_ProfileId: 0,
          TrunkGroup_LastTrunkId: 255,
          'TrunkGroup_Module;': '255,,,,,',
        };
      });
    }
    return trunckData.filter(row => !!row);
  };

  const getAuthenticationData = (data = []) => {
    let authData = [];
    if (gatewayModelSelected?.ports > 24) {
      let count = 0;
      let cycle = 0;
      authData = data.map((row, index) => {
        count = count >= 72 ? 1 : count + 1;
        cycle = count == 1 ? cycle + 1 : cycle;
        if (!row.hasValidData) return null;
        return {
          'FORMAT Authentication_Index = Authentication_UserId': `Authentication ${index} = ${row.authorizationId}`,
          Authentication_UserPassword: row.password,
          Authentication_Module: cycle,
          Authentication_Port: count,
          'Authentication_PortType;': `${DEVICE_NICKNAME_FLAG};`,
        };
      });
    } else {
      authData = data.map((row, index) => {
        if (!row.hasValidData) return null;
        return {
          'FORMAT Authentication_Index = Authentication_UserId': `Authentication ${index} = ${row.authorizationId}`,
          Authentication_UserPassword: row.password,
          Authentication_Port: index + 1,
          'Authentication_PortType;': `${DEVICE_NICKNAME_FLAG};`,
        };
      });
    }
    return authData.filter(row => !!row);
  };

  const getDialPlanData = (data = []) => {
    let numberData = [];
    if (gatewayModelSelected?.ports > 24) {
      numberData = data.map((row, index) => {
        if (!row.hasValidData) return null;
        return {
          'FORMAT Index = DialPlanName': `DialPlanRule ${index} = Extensions`,
          RuleIndex: index,
          Name: '',
          Prefix: `${row.extension}#`,
          'Tag;': `Number=${row.userName};`,
        };
      });
    } else {
      numberData = data.map((row, index) => {
        if (!row.hasValidData) return null;
        return {
          'FORMAT NumberMapIp2Tel_Index = NumberMapIp2Tel_DestinationPrefix': `NumberMapIp2Tel ${index} = ${row.extension}`,
          NumberMapIp2Tel_SourcePrefix: '*',
          NumberMapIp2Tel_SourceAddress: '*',
          NumberMapIp2Tel_SrcHost: '*',
          NumberMapIp2Tel_DestHost: '*',
          NumberMapIp2Tel_NumberType: 255,
          NumberMapIp2Tel_NumberPlan: 255,
          NumberMapIp2Tel_RemoveFromLeft: 0,
          NumberMapIp2Tel_RemoveFromRight: 0,
          NumberMapIp2Tel_LeaveFromRight: 0,
          NumberMapIp2Tel_Prefix2Add: row.userName,
          NumberMapIp2Tel_Suffix2Add: '',
          NumberMapIp2Tel_IsPresentationRestricted: '255;',
        };
      });
    }
    return numberData.filter(row => !!row);
  };

  const getFormattedArray = (arr = []) => {
    let formatedArray = [];
    let headers = [];
    Object.keys(arr[0] || {}).map(item => headers.push(item));
    formatedArray.push(headers);
    arr.map(row => {
      let values = [];
      Object.values(row || {}).map(item => values.push(item));
      formatedArray.push(values);
    });
    return formatedArray;
  };

  const getArrayToString = (arr = []) => {
    let csvContent = '';
    arr.map((row, index) => {
      csvContent += row.join(',');
      csvContent += index === arr.length - 1 ? '\n\n' : '\n';
    });
    return csvContent;
  };

  const getCSVFormattedData = (arr1 = [], arry2 = [], arr3 = []) => {
    let csvContent = '';
    csvContent += '[ TrunkGroup ]\n\n';
    let trunckData = getFormattedArray(arr1);
    csvContent += getArrayToString(trunckData);
    csvContent +=
      gatewayModelSelected?.ports > 24
        ? '[ \\TrunkGroup ]\n\n'
        : '[ \\TrunkGroup ]\n\n\n';
    csvContent += '[ Authentication ]\n\n';
    let authData = getFormattedArray(arry2);
    csvContent += getArrayToString(authData);
    csvContent += '[ \\Authentication ]\n\n';
    csvContent +=
      gatewayModelSelected?.ports > 24
        ? '[ DialPlanRule ]\n\n'
        : '[ NumberMapIp2Tel ]\n\n';
    let numData = getFormattedArray(arr3);
    csvContent += getArrayToString(numData);
    csvContent +=
      gatewayModelSelected?.ports > 24
        ? '[ \\DialPlanRule ]'
        : '[ \\NumberMapIp2Tel ]';
    return csvContent;
  };

  const handleAudioExport = () => {
    let preparedData = getPreparedData();
    const TrunkGroupData = getTrunkGroupData(preparedData);
    const authenticationData = getAuthenticationData(preparedData);
    const dialPlanRule = getDialPlanData(preparedData);

    let data = getCSVFormattedData(
      TrunkGroupData,
      authenticationData,
      dialPlanRule
    );
    let hiddenElement = document.createElement('a');
    hiddenElement.href =
      'data:text/csv;charset=utf-8,' + encodeURIComponent(data);
    hiddenElement.target = '_blank';
    hiddenElement.download = `${state.audioFileName}.${state.audioFileType?.value}`;
    hiddenElement.click();
    setState(defaultState);
  };

  const isError = !!entries.length
    ? !!entries.find(
        entry => !entry.extension || entry.extension.extensionNumber === null
      )
    : true;
  const gridData = entries.slice(
    (pageNumber - 1) * pageSize,
    (pageNumber - 1) * pageSize + pageSize
  );
  return (
    <>
      <Paper className='mr-10 ml-10 p-6 mb-2' elevation={paperElevation}>
        <div className='d-flex f-justify-between f-align-center'>
          <Typography variant='body1' className='bold-font mt-4'>
            Gateway Model
          </Typography>
          <Button
            className='ml-4'
            variant='contained'
            color='primary'
            startIcon={<SystemUpdateAltIcon />}
            disabled={
              entries.filter(
                entry =>
                  entry.extension && entry.extension.extensionNumber !== null
              ).length < 1
            }
            onClick={() => {
              setState(prevState => ({
                ...prevState,
                downloadAudioFile: true,
              }));
            }}
          >
            Download
          </Button>
        </div>
        <div className='d-flex f-align-center w-50'>
          <div className='w-50'>
            <Autocomplete
              fullWidth
              getOptionLabel={option => {
                return option.label || '';
              }}
              disableClearable
              options={AUDIO_CODES_LIST}
              placeholder='Select Gateway Model'
              value={gatewayModelSelected}
              onChange={handleGatewayChange}
            />
          </div>
          {gatewayModelSelected && (
            <Typography
              variant='body2'
              className={clsx('ml-4 text-bold', {
                'color-error': isError,
                'color-success': !isError,
              })}
            >
              {entries.filter(
                entry =>
                  entry.extension && entry.extension.extensionNumber !== null
              ).length
                ? `${
                    isError
                      ? gatewayModelSelected.ports -
                        entries.filter(
                          entry =>
                            entry.extension &&
                            entry.extension.extensionNumber !== null
                        ).length
                      : gatewayModelSelected.ports
                  } Ports ${isError ? 'Unassigned' : 'Assigned'}`
                : `${gatewayModelSelected.ports} Ports`}
            </Typography>
          )}
        </div>
        {!!entries.length && (
          <div className='mt-4'>
            <Grid
              columns={columns}
              data={gridData}
              hasSelection={false}
              showRowDivider
              pageSize={pageSize}
              pageNumber={pageNumber}
              total={entries.length}
              onPageSizeChange={handlePageSizeChange}
              onPageNumberChange={handlePageNumberChange}
              classes={{
                container: classes.audioGridPaper,
              }}
            />
          </div>
        )}
      </Paper>
      <Dialog
        classes={{
          paper: classes.dowloadModal,
        }}
        open={state.downloadAudioFile}
        onClose={() => setState(defaultState)}
      >
        <Dialog.Title hasClose />
        <Dialog.Content>
          <Typography variant='h5' className='text-bold'>
            Name Report
          </Typography>
          <Typography variant='body1' className='bold-font mt-4'>
            File Name:
          </Typography>
          <Input
            autoFocus
            type='text'
            placeholder='Enter a file name'
            name='audioFileName'
            fullWidth
            value={state.audioFileName}
            onChange={(event, error) => {
              let field = event.currentTarget.name;
              let value = event.currentTarget.value;
              setState(prevState => {
                return {
                  ...prevState,
                  audioFileName: value,
                  audioFileNameError: error,
                };
              });
            }}
            validations={{
              [VALIDATIONS.MAX_LENGTH]: 64,
              [VALIDATIONS.REQUIRED]: true,
            }}
          />
          <Typography variant='body1' className='bold-font mt-4'>
            File Type:
          </Typography>
          <Autocomplete
            fullWidth
            getOptionLabel={option => {
              return option.label || '';
            }}
            disableClearable
            options={AUDIOCODES_FILETYPE}
            placeholder='Select File Type'
            value={state.audioFileType}
            onChange={(event, selectedOption) => {
              setState(prevState => ({
                ...prevState,
                audioFileType: selectedOption,
              }));
            }}
          />
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            variant='outlined'
            className='mr-4 mb-4'
            onClick={() => {
              setState(defaultState);
            }}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            className='mr-4 mb-4'
            onClick={handleAudioExport}
            disabled={
              !state.audioFileType ||
              !state.audioFileName ||
              state.audioFileNameError
            }
          >
            Download
          </Button>
        </Dialog.Actions>
      </Dialog>
    </>
  );
};
export default AudioCodesComponent;
